import { Configuration, LogLevel, PopupRequest, RedirectRequest } from '@azure/msal-browser';

import { environmentService } from '@/services/EnvironmentService';

const b2cSubdomain = import.meta.env.VITE_AZURE_B2C_SUBDOMAIN;
const clientId = import.meta.env.VITE_AZURE_B2C_CLIENT_ID;
const publicSiteUrl = import.meta.env.VITE_PUBLIC_SITE || window.location.origin;

/**
 * Azure AD B2C is only wired up when the tenant subdomain + client id are
 * configured. In environments where they are absent (e.g. this dev workspace
 * before the app registration is provisioned) the private app degrades to a
 * clear "sign-in unavailable" state instead of constructing an invalid MSAL
 * instance and crashing the whole app.
 */
export const authConfigured = Boolean(clientId && b2cSubdomain);

export const redirectUri = `${window.location.origin}/auth`;
export const silentRedirectUri = `${window.location.origin}/silent.html`;

// Dev aid: the exact redirect URIs the Azure AD B2C app registration must know
// about are derived from the live browser origin, so they change with the
// preview URL. The on-screen list only appears on the "sign-in unavailable"
// screen (i.e. when B2C is NOT configured). When it *is* configured — as in the
// Replit preview — log them to the browser console so they are still easy to
// find and register. See docs/signed-in-preview.md.
if (import.meta.env.DEV) {
  console.info(
    `[auth] Redirect URIs to register in the Azure AD B2C app registration for this origin:\n  ${redirectUri}\n  ${silentRedirectUri}`
  );
}

/**
 * To learn more about user flows, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/user-flow-overview
 * To learn more about custom policies, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/custom-policy-overview
 */
export const b2cPolicies = {
  names: {
    // Same name used should be used in all 3 environments
    signUpSignIn: 'B2C_1_sign_up_sign_in',
    forgotPassword: 'B2C_1_password_reset',
    signUp: 'B2C_1_sign_up',
  },
  authorities: {
    signUpSignIn: {
      authority: `https://${b2cSubdomain}.b2clogin.com/${b2cSubdomain}.onmicrosoft.com/B2C_1_sign_up_sign_in`,
    },
    forgotPassword: {
      authority: `https://${b2cSubdomain}.b2clogin.com/${b2cSubdomain}.onmicrosoft.com/B2C_1_password_reset`,
    },
    signUp: {
      authority: `https://${b2cSubdomain}.b2clogin.com/${b2cSubdomain}.onmicrosoft.com/B2C_1_sign_up`,
    },
  },
  authorityDomains: [`${b2cSubdomain}.b2clogin.com`, `${b2cSubdomain}.onmicrosoft.com`],
};

/**
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const msalConfig: Configuration = {
  auth: {
    clientId,
    authority: b2cPolicies.authorities.signUpSignIn.authority,
    knownAuthorities: b2cPolicies.authorityDomains,
    redirectUri,
    postLogoutRedirectUri: publicSiteUrl,
  },
  cache: {
    cacheLocation: 'localStorage',
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }

        if (environmentService.isProduction) {
          switch (level) {
            case LogLevel.Error:
              console.error(message);
              return;
            default:
              return;
          }
        }

        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
          case LogLevel.Info:
            console.info(message);
            return;
          case LogLevel.Verbose:
            console.debug(message);
            return;
          case LogLevel.Warning:
            console.warn(message);
            return;
          default:
            return;
        }
      },
    },
  },
};

/**
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
  scopes: [`https://${b2cSubdomain}.onmicrosoft.com/web-api/access_as_user`],
};

export function createLoginRedirectRequest(returnUrl: string = '/dashboard'): RedirectRequest {
  return {
    ...loginRequest,
    redirectStartPage: redirectUri,
    state: JSON.stringify({ returnUrl }),
  };
}

/**
 * Popup variant of {@link createLoginRedirectRequest}. Used when the app runs
 * framed (the Replit preview), where MSAL refuses a full-page redirect. The
 * popup opens as a new top-level window and returns to the configured
 * `redirectUri` (`/auth`), so there is no `redirectStartPage` to set.
 */
export function createLoginPopupRequest(returnUrl: string = '/dashboard'): PopupRequest {
  return {
    ...loginRequest,
    state: JSON.stringify({ returnUrl }),
  };
}
